import React, { createContext, useContext, useMemo } from "react"
import { useFeatureFlags, flags } from "../../lib/feature-flags"
import { isTerminated } from "../utils"
import { useProgramTermination } from "./programTermination"

const IDLE_PROGRAM = {
  termination: null,
  programStatus: null,
  hasProgram: false,
  timedOut: false,
  latencyMs: null,
  loading: false,
  error: undefined,
}

const TerminationStatusContext = createContext({
  useProgramStatus: false,
  program: IDLE_PROGRAM,
  active: false,
})

const V3_STATUS_BY_PROGRAM_STATUS = {
  ACTIVE: "Active",
  SUSPENDED: "Suspended",
}

/**
 * Returns `me` with `termination` taken from pga-backend, so every V3
 * `me.termination` reader agrees with the Terminated gate. A person pga-backend
 * calls TERMINATED gets the program-derived termination. A person V3 calls
 * Terminated but pga-backend does not keeps the V3 type and takes the
 * pga-backend status (Suspended, otherwise Active, matching the gate, which
 * treats any program that is not TERMINATED as not terminated), with the
 * termination code and date cleared.
 */
export const overlayTermination = (me, { active, program }) => {
  if (!me || !active) return me
  if (program.termination) return { ...me, termination: program.termination }
  if (!isTerminated(me)) return me
  return {
    ...me,
    termination: {
      ...me.termination,
      status: V3_STATUS_BY_PROGRAM_STATUS[program.programStatus] || "Active",
      terminateCd: null,
      terminateDate: null,
    },
  }
}

/**
 * Runs the program-status query once for the whole app so the Terminated gate
 * and every other V3 `me.termination` reader share one decision. It sits above
 * the router and the Segment wrapper, and falls back to V3 (`active` false)
 * while loading or when the query fails, times out, or finds no program.
 */
export const TerminationStatusProvider = ({ authReady, me, children }) => {
  const [useProgramStatus] = useFeatureFlags([
    flags.FLAG_FEAT_TERMINATED_PROGRAM_STATUS,
  ])
  const program = useProgramTermination({
    skip: !authReady || !me || !useProgramStatus,
  })
  const active =
    !!useProgramStatus &&
    !!me &&
    !program.loading &&
    !program.error &&
    program.hasProgram

  const value = useMemo(
    () => ({ useProgramStatus: !!useProgramStatus, program, active }),
    [useProgramStatus, program, active],
  )

  return (
    <TerminationStatusContext.Provider value={value}>
      {children}
    </TerminationStatusContext.Provider>
  )
}

export const useTerminationStatus = () => useContext(TerminationStatusContext)

export const useEffectiveMe = me => {
  const { active, program } = useTerminationStatus()
  return useMemo(
    () => overlayTermination(me, { active, program }),
    [me, active, program],
  )
}
